/**
 * The audience-agent conversation pane — the audience twin of `DashboardChatSidebar`. Chat is the
 * SOLE mutation path for an audience definition (the left pane is read-only), so this panel is the
 * primary surface of the artifact page, not an optional sidecar.
 */
import { MessageSquareText } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import type { InteractiveAnswer, PendingBlock, SSEMessage } from '@/_shared/types';

import { PendingQuestion } from '../../chat/components/interactive/PendingQuestion';
import { MessageInput } from '../../chat/components/MessageInput';
import { MessageList } from '../../chat/components/MessageList';
import { useChatFeedback } from '../../chat/hooks/useChatFeedback';
import { useRefreshChatFeedbackOnIdle } from '../../chat/hooks/useRefreshChatFeedbackOnIdle';
import type { TurnRecoveryStatus } from '../../chat/hooks/useSSE';

const SUGGESTED_PROMPTS = [
  'Add a rule for users active in the last 30 days',
  'Narrow this to the Enterprise plan only',
  'Explain what this audience selects',
] as const;

interface AudienceChatSidebarProps {
  recoveryStatus?: TurnRecoveryStatus;
  onRetryRecovery?: () => void;
  onResetChat?: () => void;
  /** Current audience-chat session; feedback is scoped to it. */
  sessionId: string | null;
  messages: SSEMessage[];
  agentActive: boolean;
  /** True when a confirmed turn ID is set — gates the Stop button. */
  canStop?: boolean;
  stopping?: boolean;
  stopStatusError?: boolean;
  stopStalled?: boolean;
  onRetryStopStatus?: () => void;
  /** Disable input until the session is ready. */
  disabled?: boolean;
  /**
   * Demo workspace only: this thread hit its LLM budget, so the SERVER will refuse the next turn
   * (`audience-chat.ts` -> `isBudgetExhausted`). Without this the composer stays enabled here and the
   * refusal only surfaces as a bare FORBIDDEN toast after the user has already typed and sent.
   */
  budgetExhausted?: boolean;
  onSend: (content: string) => void;
  onStop?: () => void;
  prefillValue?: string;
  prefillNonce?: number;
  /** Interactive blocks (AI-411): the question/approval block gating the composer, if any. */
  pendingBlock?: PendingBlock | null;
  /** Submit an answer for the active interactive block. */
  onResolveBlock?: (block: PendingBlock, answer: InteractiveAnswer) => void;
  /** True while the block's answer is in flight — locks its controls. */
  blockSubmitting?: boolean;
  /** Bumped when a block resolves so the composer refocuses once it re-enables (a11y). */
  composerFocusNonce?: number;
}

function EmptyState({
  disabled,
  onSend,
}: {
  disabled: boolean;
  onSend: (content: string) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
        <MessageSquareText size={20} />
      </div>
      <p className="text-sm font-medium text-foreground">Refine this audience</p>
      <p className="mt-1 text-center text-xs text-muted-foreground">
        Ask Lookout to add, change, or explain a rule — every edit is re-counted before you save.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {SUGGESTED_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            onClick={() => onSend(prompt)}
            disabled={disabled}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand/30 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AudienceChatSidebar({
  recoveryStatus = null,
  onRetryRecovery,
  onResetChat,
  sessionId,
  messages,
  agentActive,
  canStop = false,
  stopping = false,
  stopStatusError = false,
  stopStalled = false,
  onRetryStopStatus,
  disabled = false,
  budgetExhausted = false,
  onSend,
  onStop,
  prefillValue,
  prefillNonce,
  pendingBlock = null,
  onResolveBlock,
  blockSubmitting = false,
  composerFocusNonce,
}: AudienceChatSidebarProps): React.ReactElement {
  const hasMessages = messages.length > 0;
  const recovering = recoveryStatus !== null;
  const questionPending = pendingBlock !== null && !!onResolveBlock;
  const feedback = useChatFeedback(sessionId);
  useRefreshChatFeedbackOnIdle(sessionId, agentActive, feedback.refresh);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {hasMessages ? (
        <MessageList
          messages={messages}
          suggestionSurface="audience"
          // Suppress the thinking shimmer while a block gates the composer — the turn is paused waiting
          // on the user, not working. Mirrors ChatConversation's `thinking={active && !pendingBlock}`.
          thinking={agentActive && !pendingBlock && recoveryStatus === null}
          onAction={onSend}
          compact
          actionsDisabled={agentActive || recoveryStatus !== null}
          feedback={{
            canRate: feedback.canRate,
            eligibleTurnIds: feedback.eligibleTurnIds,
            ratingByTurn: feedback.ratingByTurn,
            isPending: feedback.isPending,
            onRate: feedback.setRating,
          }}
        />
      ) : (
        <EmptyState disabled={disabled} onSend={onSend} />
      )}
      <div className="flex flex-col gap-2">
        {/* The recovery notice and an active interactive block sit above the composer rather than in
            its place, so a draft typed during the turn survives them. A reconnecting thread locks the
            field; a question leaves it open, and Send stays unavailable until the turn finishes. */}
        {recoveryStatus !== null ? (
          <div className="px-3 pt-3">
            <Callout variant={recoveryStatus === 'recovering' ? 'info' : 'warning'}>
              {recoveryStatus === 'recovering' ? (
                'Reconnecting to this conversation…'
              ) : (
                <div className="flex flex-col gap-2">
                  <span>This turn is unavailable. Try reconnecting, or reset the chat to start again.</span>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size="sm" onClick={onRetryRecovery}>
                      Reconnect
                    </Button>
                    <Button variant="secondary" size="sm" onClick={onResetChat}>
                      Reset chat
                    </Button>
                  </div>
                </div>
              )}
            </Callout>
          </div>
        ) : questionPending ? (
          // The user must answer, skip or dismiss the block before the turn can go on. Stop stays in
          // the composer below.
          <div className="px-3 pt-3">
            <PendingQuestion
              block={pendingBlock}
              onResolve={(answer) => onResolveBlock(pendingBlock, answer)}
              submitting={blockSubmitting}
            />
          </div>
        ) : budgetExhausted ? (
          <div className="px-3 pt-3">
            <Callout variant="info">
              The budget for this thread is over. Start a new chat to continue exploring.
            </Callout>
          </div>
        ) : null}
        <MessageInput
          onSend={onSend}
          disabled={agentActive || disabled || budgetExhausted || questionPending || recovering}
          // A running turn blocks Send and Enter, and leaves the field open so the next message can be
          // drafted. A session that is not ready, a thread that is over budget, or one that is
          // reconnecting still locks the field.
          locked={disabled || budgetExhausted || recovering}
          agentLoaded={canStop}
          stopping={stopping}
          stopStatusError={stopStatusError}
          stopStalled={stopStalled}
          onRetryStopStatus={onRetryStopStatus}
          variant="sidebar"
          // Override the generic disabled placeholder — this thread isn't busy, it's over, and the
          // Callout above already says why. Same wording as ChatConversation.
          placeholder={budgetExhausted ? 'This thread is closed' : 'Ask Lookout to refine this audience…'}
          onStop={canStop ? onStop : undefined}
          prefillValue={prefillValue}
          prefillNonce={prefillNonce}
          // Held while a question card is open: a failed answer leaves the card up, and focus belongs
          // there. The composer takes focus once the card closes.
          autoFocusNonce={questionPending ? 0 : composerFocusNonce}
        />
      </div>
    </div>
  );
}
